import React, { useState } from 'react';
import { Button } from 'primereact/button';
import { format } from 'date-fns';
import CustomTooltip from '../../../shared/components/common/tooltip/CustomTooltip';
import useExport from '../../../shared/hooks/global/UseExport';
import usePermission from '../../../shared/hooks/global/UsePermission';
import { downloadBlobAsFile } from '../../../shared/utils/utils';
import { AdsExportView, AdsFilters, exportAdsCsv } from '../../../shared/api/services/ads/AdsService';

interface AdsExportButtonProps {
  /** Unique element id (tooltip target) — one per section. */
  id: string;
  projectId: number;
  view: AdsExportView;
  startDate: Date | null;
  endDate: Date | null;
  filters: AdsFilters;
  /** False = nothing to export; the click is a no-op (the backend answers 400 anyway). */
  hasRows: boolean;
}

/** CSV export action button (markup/gating verbatim from the Sources tables). */
const AdsExportButton: React.FC<AdsExportButtonProps> = ({
  id,
  projectId,
  view,
  startDate,
  endDate,
  filters,
  hasRows,
}) => {
  const { canExport } = useExport();
  const projectsAccess = usePermission('PROJECTS');
  const [isDownloading, setIsDownloading] = useState(false);

  const handleExport = async () => {
    if (canExport !== true || isDownloading || !startDate || !endDate || !hasRows) {
      return;
    }
    setIsDownloading(true);
    try {
      const blob = await exportAdsCsv(projectId, view, startDate, endDate, filters);
      const fromPart = format(startDate, 'yyyy-MM-dd');
      const toPart = format(endDate, 'yyyy-MM-dd');
      downloadBlobAsFile(blob, `ads-${view}-${projectId}-${fromPart}-${toPart}.csv`);
    } catch (err) {
      console.error(err);
    } finally {
      setIsDownloading(false);
    }
  };

  // Eksport paid-results jest dla gościa zamknięty na backendzie (`exportCsv` bez `allowGuest`).
  if (!projectsAccess.write) {
    return null;
  }

  return (
    <>
      <Button
        id={id}
        className={`action-btn ${canExport !== true ? 'btn--disabled-tooltip' : ''}`}
        onClick={handleExport}
      >
        {isDownloading ? (
          <span className="ico--loader ico--xs" />
        ) : (
          <span className={`ico--download ${canExport !== true ? 'ico--disabled' : ''} ico--xs`} />
        )}
      </Button>
      <CustomTooltip tooltipText={canExport !== true ? 'upgradePlanTrialExportMsg' : 'exportCsv'} targetRef={id} />
    </>
  );
};

export default AdsExportButton;
